import axios from '../axios';
import { toFormData, type FormPayload } from './formData';
import type {
  ApiEnvelope,
  Identifier,
  ListParams,
  OwnerProjectDto,
  OwnerProjectPayload,
  PaginatedResponse,
  UpdateOwnerProjectPayload,
} from './types';

const API_URL = '/v1/owner/manpro/projects';

const hasFiles = (payload: OwnerProjectPayload | UpdateOwnerProjectPayload) =>
  Boolean(payload.documents?.length);

const serializeOwnerProjectPayload = (
  payload: OwnerProjectPayload | UpdateOwnerProjectPayload,
) => {
  const formPayload: FormPayload = {
    ...payload,
    documents: payload.documents,
    document_categories: payload.document_categories,
    requirements: payload.requirements,
  };

  return toFormData(formPayload);
};

const ownerProjectService = {
  async getOwnerProjects(params?: ListParams) {
    const response = await axios.get<ApiEnvelope<PaginatedResponse<OwnerProjectDto>>>(
      API_URL,
      { params },
    );

    return response.data.content;
  },

  async getOwnerProjectSummary() {
    const response = await axios.get<ApiEnvelope<any>>(`${API_URL}/summary`);
    return response.data.content;
  },

  async getOwnerProject(id: Identifier) {
    const response = await axios.get<ApiEnvelope<OwnerProjectDto>>(`${API_URL}/${id}`);
    return response.data.content;
  },

  async createOwnerProject(payload: OwnerProjectPayload) {
    const body = serializeOwnerProjectPayload(payload);
    const response = await axios.post<ApiEnvelope<OwnerProjectDto>>(API_URL, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data.content;
  },

  async updateOwnerProject(id: Identifier, payload: UpdateOwnerProjectPayload) {
    const hasFilesToUpload = hasFiles(payload);

    if (hasFilesToUpload) {
      const body = serializeOwnerProjectPayload(payload);
      // PHP / Laravel cannot parse multipart/form-data on PUT/PATCH requests
      // We must send as POST with _method spoofing
      body.append('_method', 'PATCH');

      const response = await axios.post<ApiEnvelope<OwnerProjectDto>>(
        `${API_URL}/${id}`,
        body,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.content;
    }

    // If no files, standard application/json PATCH is safe
    const response = await axios.patch<ApiEnvelope<OwnerProjectDto>>(
      `${API_URL}/${id}`,
      payload,
    );

    return response.data.content;
  },

  async deleteOwnerProject(id: Identifier) {
    const response = await axios.delete<ApiEnvelope<unknown>>(`${API_URL}/${id}`);
    return response.data.content;
  },

  async publishOwnerProject(id: Identifier) {
    const response = await axios.post<ApiEnvelope<OwnerProjectDto>>(`${API_URL}/${id}/publish`);
    return response.data.content;
  },

  async finalizeOwnerProject(id: Identifier) {
    const response = await axios.post<ApiEnvelope<OwnerProjectDto>>(`${API_URL}/${id}/finalize`);
    return response.data.content;
  },

  async rejectHandover(id: Identifier) {
    const response = await axios.post<ApiEnvelope<any>>(`${API_URL}/${id}/reject-handover`);
    return response.data;
  },
};

export default ownerProjectService;
